/**
 * Mobile Gamepad Input Handler (Standalone)
 *
 * ARCHITECTURE DECISION (Shadow Architecture):
 * This is a self-contained, prop-based implementation with NO implicit external dependencies.
 * It uses callback props to communicate input events, following the Feature Controller pattern.
 *
 * WHY THIS EXISTS:
 * - The original MobileGamepadInput has TWO implicit dependencies:
 *   1. Requires <InputCapture /> to be rendered separately (silent failure if missing)
 *   2. Only works when PlaycastContext.mode.mode === 'player' (implicit state check)
 * - This caused frustration and debugging difficulties in the Quickstart flow
 * - This standalone version eliminates both implicit dependencies
 *
 * KEY DIFFERENCES FROM LEGACY MobileGamepadInput:
 * - ✅ Takes explicit `enabled` prop instead of checking PlaycastContext
 * - ✅ Uses callback prop (`onInput`) for input events (explicit, testable)
 * - ✅ No dependency on player/spectator mode
 * - ✅ No silent failures from missing dependencies
 * - ✅ Clear, explicit API - follows React best practices
 * - ✅ Purely internal state (no external store dependencies)
 *
 * USAGE:
 * ```tsx
 * // With callback for controller integration
 * <MobileGamepadInputStandalone
 *   enabled={true}
 *   onInput={(event) => inputController.handleInput(event)}
 * />
 *
 * // Simple usage (no callback, just store updates)
 * <MobileGamepadInputStandalone enabled={isPlaying} />
 * ```
 *
 * NOTE:
 * - This component does NOT render <InputCapture /> itself
 * - Parent component should render <InputCapture /> separately for keyboard/mouse
 * - This allows keyboard/mouse to work independently of mobile gamepad visibility
 *
 * MIGRATION:
 * - New features should ALWAYS use this standalone version
 * - Legacy code can continue using MobileGamepadInput for backwards compatibility
 * - Eventually, the legacy version should migrate to this pattern
 */

import { useEffect, useRef, useCallback } from 'react';
import { MobileGamepadState, gamepadStore } from './store';
import {
  ANIMATION_STEP,
  GAMEPAD_BUTTON_MAP,
  calcThumbstickXY,
  springBack,
  detectDPadButtons,
  CLEAR_DPAD_BUTTONS,
} from './MobileGamepadInputCore';

/**
 * Create initial empty gamepad state
 */
const createInitialGamepadState = (): MobileGamepadState => ({
  wButtons: 0,
  bLeftTrigger: 0,
  bRightTrigger: 0,
  sThumbLX: 0,
  sThumbLY: 0,
  sThumbRX: 0,
  sThumbRY: 0,
  touches: {},
});

/**
 * Mobile gamepad input event
 * Follows the Feature Controller pattern for explicit, type-safe input handling
 */
export interface MobileGamepadInputEvent {
  type: 'button' | 'axis' | 'trigger';
  source: 'mobile-gamepad';
  timestamp: number;

  // Button event data
  buttonIndex?: number;
  buttonPressed?: boolean;

  // Axis event data (thumbsticks)
  axisIndex?: number; // 0 = left stick X, 1 = left stick Y, 2 = right stick X, 3 = right stick Y
  axisValue?: number; // -32768 to 32767 (XInput format)

  // Trigger event data
  triggerIndex?: number; // 0 = left trigger, 1 = right trigger
  triggerValue?: number; // 0 to 255 (XInput format)

  // Full gamepad state snapshot (for debugging/analytics)
  gamepadState?: MobileGamepadState;
}

/**
 * Friendly button names using descriptive keys
 * Maps to gamepad state and HTML element IDs
 * Matches the naming convention used in GamepadLayout for consistency
 */
export enum GamepadButton {
  // Triggers
  leftTrigger = 'leftTrigger',
  rightTrigger = 'rightTrigger',

  // Bumpers
  leftBumper = 'leftBumper',
  rightBumper = 'rightBumper',

  // Face buttons (Xbox layout)
  faceButtonA = 'faceButtonA',      // South button (A)
  faceButtonB = 'faceButtonB',      // East button (B)
  faceButtonX = 'faceButtonX',      // West button (X)
  faceButtonY = 'faceButtonY',      // North button (Y)

  // D-Pad
  dpadUp = 'dpadUp',
  dpadDown = 'dpadDown',
  dpadLeft = 'dpadLeft',
  dpadRight = 'dpadRight',

  // Stick clicks (L3/R3)
  leftStick = 'leftStick',
  rightStick = 'rightStick',

  // Meta buttons
  leftMeta = 'leftMeta',    // Select button
  rightMeta = 'rightMeta',  // Start button
}

/**
 * Button configuration mapping
 * Maps friendly names to HTML element IDs and state manipulation functions
 */
interface ButtonConfig {
  elementId: string;
  applyPress: (state: MobileGamepadState) => MobileGamepadState;
  applyRelease: (state: MobileGamepadState) => MobileGamepadState;
}

const BUTTON_CONFIG: Record<GamepadButton, ButtonConfig> = {
  // Triggers
  [GamepadButton.leftTrigger]: {
    elementId: 'left-ButtonTrigger',
    applyPress: (state) => ({ ...state, bLeftTrigger: 255 }),
    applyRelease: (state) => ({ ...state, bLeftTrigger: 0 }),
  },
  [GamepadButton.rightTrigger]: {
    elementId: 'right-ButtonTrigger',
    applyPress: (state) => ({ ...state, bRightTrigger: 255 }),
    applyRelease: (state) => ({ ...state, bRightTrigger: 0 }),
  },

  // Bumpers
  [GamepadButton.leftBumper]: {
    elementId: 'left-ButtonBumper',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['left-ButtonBumper'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['left-ButtonBumper'] }),
  },
  [GamepadButton.rightBumper]: {
    elementId: 'right-ButtonBumper',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonBumper'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonBumper'] }),
  },

  // Face Buttons
  [GamepadButton.faceButtonA]: {
    elementId: 'right-ButtonSouth',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonSouth'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonSouth'] }),
  },
  [GamepadButton.faceButtonB]: {
    elementId: 'right-ButtonEast',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonEast'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonEast'] }),
  },
  [GamepadButton.faceButtonX]: {
    elementId: 'right-ButtonWest',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonWest'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonWest'] }),
  },
  [GamepadButton.faceButtonY]: {
    elementId: 'right-ButtonNorth',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonNorth'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonNorth'] }),
  },

  // D-Pad (use bitflags directly)
  [GamepadButton.dpadUp]: {
    elementId: 'dpad-up',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | 0x0001 }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~0x0001 }),
  },
  [GamepadButton.dpadDown]: {
    elementId: 'dpad-down',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | 0x0002 }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~0x0002 }),
  },
  [GamepadButton.dpadLeft]: {
    elementId: 'dpad-left',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | 0x0004 }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~0x0004 }),
  },
  [GamepadButton.dpadRight]: {
    elementId: 'dpad-right',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | 0x0008 }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~0x0008 }),
  },

  // Stick clicks
  [GamepadButton.leftStick]: {
    elementId: 'left-StickClick',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['left-StickClick'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['left-StickClick'] }),
  },
  [GamepadButton.rightStick]: {
    elementId: 'right-StickClick',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-StickClick'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-StickClick'] }),
  },

  // Meta buttons
  [GamepadButton.leftMeta]: {
    elementId: 'left-ButtonMeta',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['left-ButtonMeta'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['left-ButtonMeta'] }),
  },
  [GamepadButton.rightMeta]: {
    elementId: 'right-ButtonMeta',
    applyPress: (state) => ({ ...state, wButtons: state.wButtons | GAMEPAD_BUTTON_MAP['right-ButtonMeta'] }),
    applyRelease: (state) => ({ ...state, wButtons: state.wButtons & ~GAMEPAD_BUTTON_MAP['right-ButtonMeta'] }),
  },
};

/**
 * Get button enum by HTML element ID
 */
function getButtonByElementId(elementId: string): GamepadButton | null {
  for (const [button, config] of Object.entries(BUTTON_CONFIG)) {
    if (config.elementId === elementId) {
      return button as GamepadButton;
    }
  }
  return null;
}

/**
 * Get button config by friendly name
 */
function getButtonConfig(button: GamepadButton): ButtonConfig | null {
  return BUTTON_CONFIG[button] || null;
}

/**
 * Minimum enforced delay (ms) for button chains that have a prerequisite.
 * Prevents "quick tap fires both buttons" bugs (e.g., shoot also placing items)
 * when chainDelayMs is accidentally omitted or set to 0 in campaign config.
 */
const MIN_CHAIN_DELAY_MS = 100;

/**
 * Button chaining configuration
 * Allows one button to automatically trigger another button first
 */
export type ButtonChainRule = {
  /** The button that triggers the chain (e.g., 'rightTrigger') */
  trigger: GamepadButton | string;

  /** The button that should be pressed first/simultaneously (e.g., 'leftTrigger') */
  prerequisite: GamepadButton | string;

  /** Keep prerequisite held while trigger is held (default: true) */
  maintainWhileHeld?: boolean;

  /** Delay in ms between pressing prerequisite and pressing trigger (default: MIN_CHAIN_DELAY_MS) */
  chainDelayMs?: number;
};

/**
 * Legacy button name mapping for backward compatibility
 * Converts old short names (LT, RT, etc.) to new descriptive names (leftTrigger, rightTrigger, etc.)
 */
const LEGACY_BUTTON_MAP: Record<string, GamepadButton> = {
  // Triggers
  'LT': GamepadButton.leftTrigger,
  'RT': GamepadButton.rightTrigger,

  // Bumpers
  'LB': GamepadButton.leftBumper,
  'RB': GamepadButton.rightBumper,

  // Face buttons
  'A': GamepadButton.faceButtonA,
  'B': GamepadButton.faceButtonB,
  'X': GamepadButton.faceButtonX,
  'Y': GamepadButton.faceButtonY,

  // D-Pad
  'DpadUp': GamepadButton.dpadUp,
  'DpadDown': GamepadButton.dpadDown,
  'DpadLeft': GamepadButton.dpadLeft,
  'DpadRight': GamepadButton.dpadRight,

  // Stick clicks
  'LS': GamepadButton.leftStick,
  'RS': GamepadButton.rightStick,

  // Meta buttons
  'Select': GamepadButton.leftMeta,
  'Start': GamepadButton.rightMeta,
};

/**
 * Normalize button name - converts legacy short names to new descriptive names
 */
function normalizeButtonName(button: string | GamepadButton): GamepadButton | null {
  // If it's already a valid GamepadButton enum value, return it
  if (Object.values(GamepadButton).includes(button as GamepadButton)) {
    return button as GamepadButton;
  }

  // Try to map from legacy name
  const mapped = LEGACY_BUTTON_MAP[button];
  if (mapped) {
    return mapped;
  }

  console.warn(`[MobileGamepad] Unknown button name: ${button}`);
  return null;
}

export interface MobileGamepadInputStandaloneProps {
  enabled?: boolean;

  /**
   * Explicit access rights for input capture (recommended).
   * If provided, InputCapture will use these instead of the global store.
   * If not provided, InputCapture falls back to the store for backwards compatibility.
   */
  access?: {
    mouse?: boolean;
    keyboard?: boolean;
    touch?: boolean;
    gamepad?: boolean;
  };

  /**
   * Button chaining rules
   * Example: RT (fire) automatically triggers LT (aim) for easier mobile FPS controls
   */
  buttonChains?: ButtonChainRule[];

  /**
   * Callback for input events (Feature Controller pattern)
   * This allows the controller to track input, forward to peer, or handle other logic
   *
   * Example:
   * ```tsx
   * <MobileGamepadInputStandalone
   *   onInput={(event) => {
   *     // Track first input
   *     inputController.handleInput(event);
   *     // Forward to peer connection
   *     sendInputToPeer(event);
   *   }}
   * />
   * ```
   */
  onInput?: (event: MobileGamepadInputEvent) => void;
}

/**
 * Standalone mobile gamepad input handler with explicit prop-based control.
 *
 * Use this for new features instead of the legacy MobileGamepadInput.
 *
 * This component is TRULY standalone:
 * - No captureStore dependency (sets up own DOM listeners)
 * - No gamepadStore dependency (keeps internal state)
 * - Purely callback-based communication via onInput prop
 */
export const MobileGamepadInputStandalone = ({ enabled = true, access, buttonChains = [], onInput }: MobileGamepadInputStandaloneProps) => {
  // Internal state - no external store dependency
  const gamepadStateRef = useRef<MobileGamepadState>(createInitialGamepadState());
  const thumbstickActiveRef = useRef<{ left: boolean; right: boolean }>({ left: false, right: false });
  const mouseActiveRef = useRef<{ target: HTMLElement | null; start: { x: number; y: number } } | null>(null);
  const activeChainRef = useRef<Set<string>>(new Set());
  const pendingChainTimerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Maps touch identifier → element ID so releases work even when target is null or wrong
  const touchButtonMapRef = useRef<Map<number, string>>(new Map());

  /**
   * Find if a button has a chain rule (by element ID)
   * Supports both new descriptive names and legacy short names
   */
  const findChainRule = useCallback(
    (elementId: string): ButtonChainRule | undefined => {
      const button = getButtonByElementId(elementId);
      if (!button) return undefined;

      return buttonChains.find((rule) => {
        const normalizedTrigger = normalizeButtonName(rule.trigger);
        return normalizedTrigger === button;
      });
    },
    [buttonChains],
  );

  /**
   * Apply button chain - press prerequisite when trigger is pressed
   * NOW WORKS FOR ALL BUTTONS using generic button config!
   * Supports both new descriptive names and legacy short names
   */
  const applyButtonChain = useCallback(
    (state: MobileGamepadState, elementId: string): MobileGamepadState => {
      const chainRule = findChainRule(elementId);
      if (!chainRule) return state;

      let newState = { ...state };

      // Normalize prerequisite button name (supports legacy names)
      const normalizedPrerequisite = normalizeButtonName(chainRule.prerequisite);
      if (!normalizedPrerequisite) return state;

      // Apply prerequisite button press using generic config
      const prerequisiteConfig = getButtonConfig(normalizedPrerequisite);
      if (prerequisiteConfig) {
        newState = prerequisiteConfig.applyPress(newState);
        console.log(`[Button Chain] ${chainRule.trigger} pressed -> Auto-pressing ${chainRule.prerequisite}`);
      }

      // Mark chain as active
      activeChainRef.current.add(elementId);

      return newState;
    },
    [findChainRule],
  );

  /**
   * Release button chain - release prerequisite when trigger is released
   * NOW WORKS FOR ALL BUTTONS using generic button config!
   * Supports both new descriptive names and legacy short names
   */
  const releaseButtonChain = useCallback(
    (state: MobileGamepadState, elementId: string): MobileGamepadState => {
      const chainRule = findChainRule(elementId);
      if (!chainRule || !activeChainRef.current.has(elementId)) return state;

      let newState = { ...state };

      // Release prerequisite if configured to maintain while held
      if (chainRule.maintainWhileHeld !== false) {
        // Normalize prerequisite button name (supports legacy names)
        const normalizedPrerequisite = normalizeButtonName(chainRule.prerequisite);
        if (!normalizedPrerequisite) return state;

        const prerequisiteConfig = getButtonConfig(normalizedPrerequisite);
        if (prerequisiteConfig) {
          newState = prerequisiteConfig.applyRelease(newState);
          console.log(`[Button Chain] ${chainRule.trigger} released -> Releasing ${chainRule.prerequisite}`);
        }
      }

      // Mark chain as inactive
      activeChainRef.current.delete(elementId);

      return newState;
    },
    [findChainRule],
  );

  /**
   * Emit input event to callback (if provided)
   * Follows Feature Controller pattern for explicit input handling
   */
  const emitInputEvent = useCallback(
    (event: Omit<MobileGamepadInputEvent, 'source' | 'timestamp' | 'gamepadState'>) => {
      if (!enabled || !onInput) return;

      onInput({
        ...event,
        source: 'mobile-gamepad',
        timestamp: Date.now(),
        gamepadState: gamepadStateRef.current,
      });
    },
    [enabled, onInput],
  );

  /**
   * Update internal gamepad state
   * Applies button chains BEFORE storing so UI sees transformed state
   */
  const updateGamepadState = useCallback(
    (state: MobileGamepadState, pressedButtonId?: string, releasedButtonId?: string) => {
      if (!enabled) return;

      let finalState = { ...state };

      // Apply button chain if a trigger button was pressed
      if (pressedButtonId) {
        finalState = applyButtonChain(finalState, pressedButtonId);
      }

      // Release button chain if a trigger button was released
      if (releasedButtonId) {
        finalState = releaseButtonChain(finalState, releasedButtonId);
      }

      gamepadStateRef.current = finalState;
      // Update the store so MobileGamepadUi can display the TRANSFORMED state
      gamepadStore.updateGamepad(finalState);
    },
    [enabled, applyButtonChain, releaseButtonChain],
  );

  // Spring back animation for thumbsticks using RAF
  useEffect(() => {
    if (!enabled) return;

    let rafId: number;

    const animate = () => {
      const state = gamepadStateRef.current;
      let needsUpdate = false;

      if (!thumbstickActiveRef.current.left) {
        if (state.sThumbLX !== 0 || state.sThumbLY !== 0) {
          state.sThumbLX = springBack(state.sThumbLX, ANIMATION_STEP);
          state.sThumbLY = springBack(state.sThumbLY, ANIMATION_STEP);
          needsUpdate = true;
        }
      }

      if (!thumbstickActiveRef.current.right) {
        if (state.sThumbRX !== 0 || state.sThumbRY !== 0) {
          state.sThumbRX = springBack(state.sThumbRX, ANIMATION_STEP);
          state.sThumbRY = springBack(state.sThumbRY, ANIMATION_STEP);
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        updateGamepadState({ ...state });
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [enabled, updateGamepadState]);

  // Cleanup refs on unmount
  useEffect(() => {
    const pendingTimers = pendingChainTimerRef.current;
    const touchMap = touchButtonMapRef.current;
    return () => {
      pendingTimers.forEach((timerId) => clearTimeout(timerId));
      pendingTimers.clear();
      gamepadStateRef.current = createInitialGamepadState();
      thumbstickActiveRef.current = { left: false, right: false };
      touchMap.clear();
    };
  }, []);

  // Handle touch events with direct DOM listeners
  useEffect(() => {
    if (!enabled) return;

    const handleTouchEvent = (event: TouchEvent) => {
      const state = gamepadStateRef.current;
      const newGamepadState: MobileGamepadState = structuredClone(state);
      const touches = event.changedTouches;

      switch (event.type) {
        case 'touchstart':
          for (let i = 0; i < touches.length; i++) {
            const t = touches[i].target as HTMLElement | null;
            if (!t) continue;
            const elementId = t.id;
            const buttonId = elementId as keyof typeof GAMEPAD_BUTTON_MAP;

            // Record identifier → element mapping for reliable release
            if (elementId) {
              touchButtonMapRef.current.set(touches[i].identifier, elementId);
            }

            // Thumbstick activation
            if (elementId === 'left-Stick' || elementId === 'right-Stick') {
              thumbstickActiveRef.current[elementId === 'left-Stick' ? 'left' : 'right'] = true;
              newGamepadState.touches[touches[i].identifier] = {
                identifier: touches[i].identifier,
                start: {
                  x: touches[i].clientX,
                  y: touches[i].clientY,
                },
              };
            }

            const chainRule = findChainRule(elementId);
            const chainDelay = chainRule
              ? Math.max(chainRule.chainDelayMs ?? MIN_CHAIN_DELAY_MS, MIN_CHAIN_DELAY_MS)
              : 0;
            const deferTriggerPress = chainDelay > 0;

            if (!deferTriggerPress) {
              // Button press
              if (GAMEPAD_BUTTON_MAP[buttonId]) {
                newGamepadState.wButtons |= GAMEPAD_BUTTON_MAP[buttonId];

                const buttonIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(buttonId);
                emitInputEvent({ type: 'button', buttonIndex, buttonPressed: true });
              }

              // Trigger press
              if (elementId === 'left-ButtonTrigger' || elementId === 'right-ButtonTrigger') {
                const trigger = elementId === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
                const triggerIndex = elementId === 'left-ButtonTrigger' ? 0 : 1;
                newGamepadState[trigger] = 255;
                emitInputEvent({ type: 'trigger', triggerIndex, triggerValue: 255 });
              }

              // D-pad diagonal detection using shared logic
              const dpadButtons = detectDPadButtons(touches[i].clientX, touches[i].clientY);
              if (dpadButtons) {
                newGamepadState.wButtons |= dpadButtons;
                if (dpadButtons & 0x0001) emitInputEvent({ type: 'button', buttonIndex: 12, buttonPressed: true });
                if (dpadButtons & 0x0002) emitInputEvent({ type: 'button', buttonIndex: 13, buttonPressed: true });
                if (dpadButtons & 0x0004) emitInputEvent({ type: 'button', buttonIndex: 14, buttonPressed: true });
                if (dpadButtons & 0x0008) emitInputEvent({ type: 'button', buttonIndex: 15, buttonPressed: true });
              }
            }

            // Apply prerequisite immediately (via applyButtonChain inside updateGamepadState)
            updateGamepadState(newGamepadState, elementId);

            if (deferTriggerPress) {
              const capturedButtonId = buttonId;
              const capturedElementId = elementId;
              const timerId = setTimeout(() => {
                pendingChainTimerRef.current.delete(capturedElementId);
                const currentState = gamepadStateRef.current;
                const deferredState = { ...currentState };

                if (GAMEPAD_BUTTON_MAP[capturedButtonId]) {
                  deferredState.wButtons |= GAMEPAD_BUTTON_MAP[capturedButtonId];
                  const btnIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(capturedButtonId);
                  emitInputEvent({ type: 'button', buttonIndex: btnIndex, buttonPressed: true });
                }

                if (capturedElementId === 'left-ButtonTrigger' || capturedElementId === 'right-ButtonTrigger') {
                  const trig = capturedElementId === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
                  const trigIdx = capturedElementId === 'left-ButtonTrigger' ? 0 : 1;
                  deferredState[trig] = 255;
                  emitInputEvent({ type: 'trigger', triggerIndex: trigIdx, triggerValue: 255 });
                }

                updateGamepadState(deferredState);
              }, chainDelay);
              pendingChainTimerRef.current.set(elementId, timerId);
            }
          }
          break;

        case 'touchmove':
          for (let i = 0; i < touches.length; i++) {
            const touchIdentifier = touches[i].identifier;
            // Use identifier map for reliable element lookup
            const elementId = touchButtonMapRef.current.get(touchIdentifier)
              ?? (touches[i].target as HTMLElement | null)?.id ?? '';

            if ((elementId === 'left-Stick' || elementId === 'right-Stick') && newGamepadState.touches[touchIdentifier]) {
              const isPortrait = window.innerHeight > window.innerWidth;
              const { x, y } = calcThumbstickXY(newGamepadState.touches[touchIdentifier].start, {
                x: touches[i].clientX,
                y: touches[i].clientY,
              }, isPortrait);
              if (elementId === 'left-Stick') {
                newGamepadState.sThumbLX = x;
                newGamepadState.sThumbLY = y;
                emitInputEvent({ type: 'axis', axisIndex: 0, axisValue: x });
                emitInputEvent({ type: 'axis', axisIndex: 1, axisValue: y });
              } else {
                newGamepadState.sThumbRX = x;
                newGamepadState.sThumbRY = y;
                emitInputEvent({ type: 'axis', axisIndex: 2, axisValue: x });
                emitInputEvent({ type: 'axis', axisIndex: 3, axisValue: y });
              }
            }
          }
          updateGamepadState(newGamepadState);
          gamepadStateRef.current = newGamepadState;
          break;

        case 'touchend':
        case 'touchcancel':
          for (let i = 0; i < touches.length; i++) {
            const touchId = touches[i].identifier;
            // Use identifier map as primary source; fall back to target.id
            const elementId = touchButtonMapRef.current.get(touchId)
              ?? (touches[i].target as HTMLElement | null)?.id ?? '';
            touchButtonMapRef.current.delete(touchId);

            const buttonId = elementId as keyof typeof GAMEPAD_BUTTON_MAP;

            // Deactivate thumbstick
            if (elementId === 'left-Stick' || elementId === 'right-Stick') {
              thumbstickActiveRef.current[elementId === 'left-Stick' ? 'left' : 'right'] = false;
              delete newGamepadState.touches[touchId];
            }

            // If a chain delay timer is still pending, the trigger was never actually
            // pressed -- cancel it and only release the prerequisite.
            const pendingTimer = pendingChainTimerRef.current.get(elementId);
            if (pendingTimer !== undefined) {
              clearTimeout(pendingTimer);
              pendingChainTimerRef.current.delete(elementId);
              updateGamepadState(newGamepadState, undefined, elementId);
              continue;
            }

            // Release button
            if (GAMEPAD_BUTTON_MAP[buttonId]) {
              newGamepadState.wButtons &= ~GAMEPAD_BUTTON_MAP[buttonId];

              const buttonIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(buttonId);
              emitInputEvent({ type: 'button', buttonIndex, buttonPressed: false });
            }

            // Release trigger
            if (elementId === 'left-ButtonTrigger' || elementId === 'right-ButtonTrigger') {
              const trigger = elementId === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
              const triggerIndex = elementId === 'left-ButtonTrigger' ? 0 : 1;
              newGamepadState[trigger] = 0;
              emitInputEvent({ type: 'trigger', triggerIndex, triggerValue: 0 });
            }

            // Clear any stuck d-pad buttons using shared constant
            const prevButtons = newGamepadState.wButtons;
            newGamepadState.wButtons &= ~CLEAR_DPAD_BUTTONS;

            if (prevButtons & 0x0001) emitInputEvent({ type: 'button', buttonIndex: 12, buttonPressed: false });
            if (prevButtons & 0x0002) emitInputEvent({ type: 'button', buttonIndex: 13, buttonPressed: false });
            if (prevButtons & 0x0004) emitInputEvent({ type: 'button', buttonIndex: 14, buttonPressed: false });
            if (prevButtons & 0x0008) emitInputEvent({ type: 'button', buttonIndex: 15, buttonPressed: false });

            updateGamepadState(newGamepadState, undefined, elementId);
          }
          break;
      }
    };

    // Register touch event listeners
    window.addEventListener('touchstart', handleTouchEvent);
    window.addEventListener('touchmove', handleTouchEvent);
    window.addEventListener('touchend', handleTouchEvent);
    window.addEventListener('touchcancel', handleTouchEvent);

    return () => {
      window.removeEventListener('touchstart', handleTouchEvent);
      window.removeEventListener('touchmove', handleTouchEvent);
      window.removeEventListener('touchend', handleTouchEvent);
      window.removeEventListener('touchcancel', handleTouchEvent);
    };
  }, [enabled, updateGamepadState, emitInputEvent, findChainRule]);

  // Release all inputs when page loses visibility (e.g. app switch, notification, incoming call)
  // Without this, touchend/touchcancel may never fire and buttons stay stuck
  useEffect(() => {
    if (!enabled) return;

    const releaseAllInputs = () => {
      if (document.hidden) {
        pendingChainTimerRef.current.forEach((timerId) => clearTimeout(timerId));
        pendingChainTimerRef.current.clear();
        const resetState = createInitialGamepadState();
        gamepadStateRef.current = resetState;
        gamepadStore.updateGamepad(resetState);
        thumbstickActiveRef.current = { left: false, right: false };
        touchButtonMapRef.current.clear();
        activeChainRef.current.clear();
      }
    };

    document.addEventListener('visibilitychange', releaseAllInputs);
    window.addEventListener('blur', releaseAllInputs);

    return () => {
      document.removeEventListener('visibilitychange', releaseAllInputs);
      window.removeEventListener('blur', releaseAllInputs);
    };
  }, [enabled]);

  // Handle mouse events (for desktop debugging) with direct DOM listeners
  useEffect(() => {
    if (!enabled) return;

    // Don't register mouse handlers on mobile devices to prevent double input
    // Mobile browsers fire synthetic mouse events after touch events
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isMobile) return;

    const handleMouseEvent = (mouseEvent: MouseEvent) => {
      const state = gamepadStateRef.current;
      const newGamepadState: MobileGamepadState = structuredClone(state);
      const target = mouseEvent.target as HTMLElement;
      const buttonId = target.id as keyof typeof GAMEPAD_BUTTON_MAP;

      switch (mouseEvent.type) {
        case 'mousedown': {
          // Thumbstick activation
          if (target.id === 'left-Stick' || target.id === 'right-Stick') {
            const side = target.id === 'left-Stick' ? 'left' : 'right';
            thumbstickActiveRef.current[side] = true;
            mouseActiveRef.current = {
              target: target,
              start: {
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
              },
            };
          }

          const chainRule = findChainRule(target.id);
          const chainDelay = chainRule
            ? Math.max(chainRule.chainDelayMs ?? MIN_CHAIN_DELAY_MS, MIN_CHAIN_DELAY_MS)
            : 0;
          const deferTriggerPress = chainDelay > 0;

          if (!deferTriggerPress) {
            // Button press
            if (GAMEPAD_BUTTON_MAP[buttonId]) {
              newGamepadState.wButtons |= GAMEPAD_BUTTON_MAP[buttonId];

              const buttonIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(buttonId);
              emitInputEvent({ type: 'button', buttonIndex, buttonPressed: true });
            }

            // Trigger press
            if (target.id === 'left-ButtonTrigger' || target.id === 'right-ButtonTrigger') {
              const trigger = target.id === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
              const triggerIndex = target.id === 'left-ButtonTrigger' ? 0 : 1;
              newGamepadState[trigger] = 255;
              emitInputEvent({ type: 'trigger', triggerIndex, triggerValue: 255 });
            }

            // D-pad diagonal detection
            const dpadButtons = detectDPadButtons(mouseEvent.clientX, mouseEvent.clientY);
            if (dpadButtons) {
              newGamepadState.wButtons |= dpadButtons;

              if (dpadButtons & 0x0001) emitInputEvent({ type: 'button', buttonIndex: 12, buttonPressed: true }); // Up
              if (dpadButtons & 0x0002) emitInputEvent({ type: 'button', buttonIndex: 13, buttonPressed: true }); // Down
              if (dpadButtons & 0x0004) emitInputEvent({ type: 'button', buttonIndex: 14, buttonPressed: true }); // Left
              if (dpadButtons & 0x0008) emitInputEvent({ type: 'button', buttonIndex: 15, buttonPressed: true }); // Right
            }
          }

          updateGamepadState(newGamepadState, target.id);

          if (deferTriggerPress) {
            const capturedButtonId = buttonId;
            const capturedElementId = target.id;
            const timerId = setTimeout(() => {
              pendingChainTimerRef.current.delete(capturedElementId);
              const currentState = gamepadStateRef.current;
              const deferredState = { ...currentState };

              if (GAMEPAD_BUTTON_MAP[capturedButtonId]) {
                deferredState.wButtons |= GAMEPAD_BUTTON_MAP[capturedButtonId];
                const btnIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(capturedButtonId);
                emitInputEvent({ type: 'button', buttonIndex: btnIndex, buttonPressed: true });
              }

              if (capturedElementId === 'left-ButtonTrigger' || capturedElementId === 'right-ButtonTrigger') {
                const trig = capturedElementId === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
                const trigIdx = capturedElementId === 'left-ButtonTrigger' ? 0 : 1;
                deferredState[trig] = 255;
                emitInputEvent({ type: 'trigger', triggerIndex: trigIdx, triggerValue: 255 });
              }

              updateGamepadState(deferredState);
            }, chainDelay);
            pendingChainTimerRef.current.set(target.id, timerId);
          }
          break;
        }

        case 'mousemove': {
          // Handle thumbstick dragging
          if (mouseActiveRef.current) {
            const activeTarget = mouseActiveRef.current.target;
            if (activeTarget?.id === 'left-Stick' || activeTarget?.id === 'right-Stick') {
              const isPortrait = window.innerHeight > window.innerWidth;
              const { x, y } = calcThumbstickXY(mouseActiveRef.current.start, {
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
              }, isPortrait);
              if (activeTarget.id === 'left-Stick') {
                newGamepadState.sThumbLX = x;
                newGamepadState.sThumbLY = y;
                // Emit axis events for left stick
                emitInputEvent({ type: 'axis', axisIndex: 0, axisValue: x });
                emitInputEvent({ type: 'axis', axisIndex: 1, axisValue: y });
              } else {
                newGamepadState.sThumbRX = x;
                newGamepadState.sThumbRY = y;
                // Emit axis events for right stick
                emitInputEvent({ type: 'axis', axisIndex: 2, axisValue: x });
                emitInputEvent({ type: 'axis', axisIndex: 3, axisValue: y });
              }
              updateGamepadState(newGamepadState);
              gamepadStateRef.current = newGamepadState;
            }
          }
          break;
        }

        case 'mouseup': {
          // Release thumbstick
          if (mouseActiveRef.current) {
            const activeTarget = mouseActiveRef.current.target;
            if (activeTarget?.id === 'left-Stick' || activeTarget?.id === 'right-Stick') {
              thumbstickActiveRef.current[activeTarget.id === 'left-Stick' ? 'left' : 'right'] = false;
            }
            mouseActiveRef.current = null;
          }

          // If a chain delay timer is still pending, the trigger was never actually
          // pressed -- cancel it and only release the prerequisite.
          const pendingTimer = pendingChainTimerRef.current.get(target.id);
          if (pendingTimer !== undefined) {
            clearTimeout(pendingTimer);
            pendingChainTimerRef.current.delete(target.id);
            updateGamepadState(newGamepadState, undefined, target.id);
            break;
          }

          // Release button
          if (GAMEPAD_BUTTON_MAP[buttonId]) {
            newGamepadState.wButtons &= ~GAMEPAD_BUTTON_MAP[buttonId];

            const buttonIndex = Object.keys(GAMEPAD_BUTTON_MAP).indexOf(buttonId);
            emitInputEvent({ type: 'button', buttonIndex, buttonPressed: false });
          }

          // Release trigger
          if (target.id === 'left-ButtonTrigger' || target.id === 'right-ButtonTrigger') {
            const trigger = target.id === 'left-ButtonTrigger' ? 'bLeftTrigger' : 'bRightTrigger';
            const triggerIndex = target.id === 'left-ButtonTrigger' ? 0 : 1;
            newGamepadState[trigger] = 0;
            emitInputEvent({ type: 'trigger', triggerIndex, triggerValue: 0 });
          }

          // Clear d-pad buttons
          const prevButtons = newGamepadState.wButtons;
          newGamepadState.wButtons &= ~CLEAR_DPAD_BUTTONS;

          if (prevButtons & 0x0001) emitInputEvent({ type: 'button', buttonIndex: 12, buttonPressed: false }); // Up
          if (prevButtons & 0x0002) emitInputEvent({ type: 'button', buttonIndex: 13, buttonPressed: false }); // Down
          if (prevButtons & 0x0004) emitInputEvent({ type: 'button', buttonIndex: 14, buttonPressed: false }); // Left
          if (prevButtons & 0x0008) emitInputEvent({ type: 'button', buttonIndex: 15, buttonPressed: false }); // Right

          updateGamepadState(newGamepadState, undefined, target.id);
          break;
        }
      }
    };

    // Register mouse event listeners
    window.addEventListener('mousedown', handleMouseEvent);
    window.addEventListener('mousemove', handleMouseEvent);
    window.addEventListener('mouseup', handleMouseEvent);

    return () => {
      window.removeEventListener('mousedown', handleMouseEvent);
      window.removeEventListener('mousemove', handleMouseEvent);
      window.removeEventListener('mouseup', handleMouseEvent);
    };
  }, [enabled, updateGamepadState, emitInputEvent, findChainRule]);

  // Truly standalone - no InputCapture, no external stores
  // Parent component should render InputCapture separately as a peer component
  // This allows keyboard/mouse input to work independently of mobile gamepad visibility
  return null;
};

